import { useEffect, useState } from 'react';
import { decodeEventLog, encodeFunctionData, formatUnits, keccak256, toHex } from 'viem';
import { useAccount, useReadContract, useWaitForTransactionReceipt, useWriteContract } from 'wagmi';
import { MARKET_PAIRS, type MarketPair } from '../data/mockMarket';
import { FARM_ABI, GOVERNOR_ABI, VOTES_ABI } from '../web3/abis';
import { TARGET_CHAIN, TARGET_CHAIN_LABEL } from '../web3/config';
import { getDeployment, getFarmPid } from '../web3/contracts';
import { useTokenBalance } from '../web3/useTokenBalance';
import { useWallet } from '../web3/useWallet';
import TokenIcon from './TokenIcon';
import TrustBar from './TrustBar';

const DECIMALS = 18;

const STATE_LABEL: Record<number, string> = {
  0: 'Pending',
  1: 'Active',
  2: 'Canceled',
  3: 'Defeated',
  4: 'Succeeded',
  5: 'Queued',
  6: 'Expired',
  7: 'Executed',
};

const STATE_STYLE: Record<number, string> = {
  0: 'text-foam-500 bg-abyss-700',
  1: 'text-tide-400 bg-tide-400/10',
  2: 'text-coral-400 bg-coral-400/10',
  3: 'text-coral-400 bg-coral-400/10',
  4: 'text-tide-400 bg-tide-400/10',
  5: 'text-tide-400 bg-tide-400/10',
  6: 'text-foam-500 bg-abyss-700',
  7: 'text-tide-400 bg-tide-400/10',
};

interface ProposalLog {
  proposalId: bigint;
  proposer: `0x${string}`;
  targets: readonly `0x${string}`[];
  values: readonly bigint[];
  calldatas: readonly `0x${string}`[];
  description: string;
  voteStart: bigint;
  voteEnd: bigint;
}

function fmtVotes(value: bigint | undefined) {
  if (value === undefined) return '-';
  return Number(formatUnits(value, DECIMALS)).toLocaleString('en-US', { maximumFractionDigits: 2 });
}

// The public testnet RPC rejects ranged eth_getLogs without an archive-tier
// key, so proposal history can't be reconstructed by scanning events. Instead
// each proposal is persisted locally the moment it's created (decoded straight
// off that transaction's own receipt, which every RPC supports), seeded with
// the one proposal the deploy script created on-chain.
function storageKey(chainId: number, governor: string) {
  return `pelagos-gov-proposals-${chainId}-${governor.toLowerCase()}`;
}

function loadStoredProposals(chainId: number, governor: string): ProposalLog[] {
  try {
    const raw = localStorage.getItem(storageKey(chainId, governor));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Array<{
      proposalId: string;
      proposer: `0x${string}`;
      targets: `0x${string}`[];
      values: string[];
      calldatas: `0x${string}`[];
      description: string;
      voteStart: string;
      voteEnd: string;
    }>;
    return parsed.map((p) => ({
      ...p,
      proposalId: BigInt(p.proposalId),
      values: p.values.map(BigInt),
      voteStart: BigInt(p.voteStart),
      voteEnd: BigInt(p.voteEnd),
    }));
  } catch {
    return [];
  }
}

function saveProposal(chainId: number, governor: string, proposal: ProposalLog) {
  const existing = loadStoredProposals(chainId, governor);
  if (existing.some((p) => p.proposalId === proposal.proposalId)) return;
  const serializable = existing.concat(proposal).map((p) => ({
    ...p,
    proposalId: p.proposalId.toString(),
    values: p.values.map(String),
    voteStart: p.voteStart.toString(),
    voteEnd: p.voteEnd.toString(),
  }));
  localStorage.setItem(storageKey(chainId, governor), JSON.stringify(serializable));
}

function useProposalList(chainId: number, governor: `0x${string}` | undefined) {
  const deployment = getDeployment(chainId);
  const [, setReloadKey] = useState(0);

  const logs = (() => {
    if (!governor) return [];
    const stored = loadStoredProposals(chainId, governor);
    const seed = deployment?.seedProposal;
    if (seed && !stored.some((p) => p.proposalId.toString() === seed.id)) {
      stored.push({
        proposalId: BigInt(seed.id),
        proposer: seed.proposer,
        targets: seed.targets,
        values: seed.values.map(BigInt),
        calldatas: seed.calldatas,
        description: seed.description,
        voteStart: 0n,
        voteEnd: 0n,
      });
    }
    return stored.sort((a, b) => (a.proposalId > b.proposalId ? -1 : 1));
  })();

  return {
    logs,
    refetch: () => setReloadKey((k) => k + 1),
    record: (proposal: ProposalLog) => {
      if (governor) saveProposal(chainId, governor, proposal);
      setReloadKey((k) => k + 1);
    },
  };
}

export default function GovernanceView() {
  const { address, isConnected, chainId } = useAccount();
  const { connectWallet, isPending: isConnecting, wrongNetwork, switchToTargetChain } = useWallet();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const onCorrectChain = chainId === TARGET_CHAIN.id;
  const deployment = getDeployment(readChainId);

  const { raw: plgBalance } = useTokenBalance('PLG');

  const { data: delegatee, refetch: refetchDelegatee } = useReadContract({
    address: deployment?.tokens.PLG,
    abi: VOTES_ABI,
    functionName: 'delegates',
    args: address ? [address] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(deployment?.tokens.PLG && address) },
  });

  const { data: votingPower, refetch: refetchVotes } = useReadContract({
    address: deployment?.tokens.PLG,
    abi: VOTES_ABI,
    functionName: 'getVotes',
    args: address ? [address] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(deployment?.tokens.PLG && address), refetchInterval: 8000 },
  });

  const hasDelegated = delegatee && delegatee !== '0x0000000000000000000000000000000000000000';

  const { writeContract: writeDelegate, data: delegateHash, isPending: isDelegatePending } = useWriteContract();
  const { isLoading: isDelegateConfirming, isSuccess: isDelegateConfirmed } = useWaitForTransactionReceipt({
    hash: delegateHash,
  });

  useEffect(() => {
    if (isDelegateConfirmed) {
      refetchDelegatee();
      refetchVotes();
    }
  }, [isDelegateConfirmed]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelegate = () => {
    if (!deployment?.tokens.PLG || !address) return;
    writeDelegate({ address: deployment.tokens.PLG, abi: VOTES_ABI, functionName: 'delegate', args: [address] });
  };

  const { logs, refetch: refetchLogs, record: recordProposal } = useProposalList(readChainId, deployment?.governor);

  const [selectedId, setSelectedId] = useState<bigint | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const selectedLog = logs.find((l) => l.proposalId === selectedId) ?? logs[0];

  return (
    <div className="flex-1 flex flex-col lg:flex-row min-h-0 overflow-y-auto">
      <div className="w-full lg:w-[28rem] border-r border-abyss-700 flex-shrink-0">
        <div className="p-4 border-b border-abyss-700 flex items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold text-foam-100">Governance</h2>
            <p className="text-xs text-foam-500 mt-1">PLG 보유량 기반 온체인 투표 (Governor + Timelock).</p>
          </div>
          <button
            onClick={() => setShowCreate((v) => !v)}
            className="text-xs font-semibold px-2.5 py-1.5 rounded-md bg-tide-500/15 text-tide-400 hover:bg-tide-500/25 transition-colors flex-shrink-0"
          >
            + Propose
          </button>
        </div>

        <div className="p-4 border-b border-abyss-700 space-y-2">
          <div className="p-3 rounded-xl border border-abyss-700 bg-abyss-850">
            <div className="text-xs text-foam-500 mb-1">My voting power</div>
            <div className="text-xl font-semibold text-foam-100 tabular-nums">
              {isConnected ? `${fmtVotes(votingPower)} PLG` : '-'}
            </div>
            {isConnected && plgBalance !== undefined && plgBalance > 0n && !hasDelegated && (
              <button
                onClick={handleDelegate}
                disabled={isDelegatePending || isDelegateConfirming}
                className="mt-2 w-full py-2 rounded-lg text-xs font-semibold bg-tide-500 text-abyss-950 hover:bg-tide-400 disabled:opacity-60 transition-colors"
              >
                {isDelegatePending || isDelegateConfirming ? 'Activating...' : 'Activate voting power (delegate)'}
              </button>
            )}
          </div>
        </div>

        {showCreate && (
          <CreateProposalForm
            chainId={readChainId}
            onCreated={(proposal) => {
              recordProposal(proposal);
              setShowCreate(false);
            }}
          />
        )}

        <div className="divide-y divide-abyss-700">
          {logs.length === 0 && (
            <div className="p-4 text-xs text-foam-500">아직 등록된 제안이 없습니다.</div>
          )}
          {logs.map((log) => (
            <ProposalListItem
              key={log.proposalId.toString()}
              governor={deployment?.governor}
              log={log}
              chainId={readChainId}
              isSelected={log.proposalId === selectedLog?.proposalId}
              onSelect={() => setSelectedId(log.proposalId)}
            />
          ))}
        </div>
      </div>

      <div className="flex-1 p-6 max-w-2xl">
        {selectedLog ? (
          <ProposalDetail
            log={selectedLog}
            governor={deployment?.governor}
            chainId={readChainId}
            isConnected={isConnected}
            wrongNetwork={wrongNetwork || !onCorrectChain}
            connectWallet={connectWallet}
            switchToTargetChain={switchToTargetChain}
            isConnecting={isConnecting}
            account={address}
            onActionConfirmed={refetchLogs}
          />
        ) : (
          <div className="text-sm text-foam-500">제안을 선택하세요.</div>
        )}
        <div className="mt-6">
          <TrustBar contract={deployment?.governor} />
        </div>
      </div>
    </div>
  );
}

function ProposalListItem({
  governor,
  log,
  chainId,
  isSelected,
  onSelect,
}: {
  governor: `0x${string}` | undefined;
  log: ProposalLog;
  chainId: number;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const { data: state } = useReadContract({
    address: governor,
    abi: GOVERNOR_ABI,
    functionName: 'state',
    args: [log.proposalId],
    chainId,
    query: { enabled: Boolean(governor), refetchInterval: 10000 },
  });

  const stateNum = state !== undefined ? Number(state) : 0;

  return (
    <button
      onClick={onSelect}
      className={`w-full text-left p-4 transition-colors ${isSelected ? 'bg-tide-400/10' : 'hover:bg-abyss-800/60'}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-xs text-foam-500">#{log.proposalId.toString().slice(0, 6)}...</span>
        {state !== undefined && (
          <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${STATE_STYLE[stateNum]}`}>
            {STATE_LABEL[stateNum]}
          </span>
        )}
      </div>
      <div className="text-sm font-medium text-foam-100 leading-snug">{log.description}</div>
    </button>
  );
}

function ProposalDetail({
  log,
  governor,
  chainId,
  isConnected,
  wrongNetwork,
  connectWallet,
  switchToTargetChain,
  isConnecting,
  account,
  onActionConfirmed,
}: {
  log: ProposalLog;
  governor: `0x${string}` | undefined;
  chainId: number;
  isConnected: boolean;
  wrongNetwork: boolean;
  connectWallet: () => void;
  switchToTargetChain: () => void;
  isConnecting: boolean;
  account: `0x${string}` | undefined;
  onActionConfirmed: () => void;
}) {
  const { data: state, refetch: refetchState } = useReadContract({
    address: governor,
    abi: GOVERNOR_ABI,
    functionName: 'state',
    args: [log.proposalId],
    chainId,
    query: { enabled: Boolean(governor), refetchInterval: 8000 },
  });

  const { data: votes, refetch: refetchVotes } = useReadContract({
    address: governor,
    abi: GOVERNOR_ABI,
    functionName: 'proposalVotes',
    args: [log.proposalId],
    chainId,
    query: { enabled: Boolean(governor), refetchInterval: 8000 },
  });

  const { data: hasVoted, refetch: refetchHasVoted } = useReadContract({
    address: governor,
    abi: GOVERNOR_ABI,
    functionName: 'hasVoted',
    args: account ? [log.proposalId, account] : undefined,
    chainId,
    query: { enabled: Boolean(governor && account) },
  });

  const { writeContract, data: hash, isPending, reset } = useWriteContract();
  const { isLoading: isConfirming, isSuccess: isConfirmed } = useWaitForTransactionReceipt({ hash });

  useEffect(() => {
    if (isConfirmed) {
      refetchState();
      refetchVotes();
      refetchHasVoted();
      onActionConfirmed();
      reset();
    }
  }, [isConfirmed]); // eslint-disable-line react-hooks/exhaustive-deps

  const stateNum = state !== undefined ? Number(state) : -1;
  const [against, forVotes, abstain] = votes ?? [0n, 0n, 0n];
  const total = against + forVotes + abstain;
  const pctFor = total > 0n ? Number((forVotes * 10000n) / total) / 100 : 0;
  const pctAgainst = total > 0n ? Number((against * 10000n) / total) / 100 : 0;
  const pctAbstain = total > 0n ? Number((abstain * 10000n) / total) / 100 : 0;

  const descriptionHash = keccak256(toHex(log.description));

  const castVote = (support: 0 | 1 | 2) => {
    if (!governor) return;
    writeContract({ address: governor, abi: GOVERNOR_ABI, functionName: 'castVote', args: [log.proposalId, support] });
  };

  const handleQueue = () => {
    if (!governor) return;
    writeContract({
      address: governor,
      abi: GOVERNOR_ABI,
      functionName: 'queue',
      args: [log.targets, log.values, log.calldatas, descriptionHash],
    });
  };

  const handleExecute = () => {
    if (!governor) return;
    writeContract({
      address: governor,
      abi: GOVERNOR_ABI,
      functionName: 'execute',
      args: [log.targets, log.values, log.calldatas, descriptionHash],
    });
  };

  const isBusy = isPending || isConfirming;

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-xs text-foam-500 break-all">Proposal #{log.proposalId.toString()}</span>
        {stateNum >= 0 && (
          <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0 ${STATE_STYLE[stateNum]}`}>
            {STATE_LABEL[stateNum]}
          </span>
        )}
      </div>

      <h1 className="text-xl font-semibold text-foam-100 mb-4">{log.description}</h1>

      <div className="flex items-center gap-2 text-xs text-foam-500 mb-6">
        <TokenIcon symbol="PLG" size={16} />
        <span className="break-all">{log.proposer}</span>
      </div>

      <div className="space-y-3 mb-6">
        <VoteBar label="For" pct={pctFor} votes={forVotes} color="bg-tide-400" />
        <VoteBar label="Against" pct={pctAgainst} votes={against} color="bg-coral-400" />
        <VoteBar label="Abstain" pct={pctAbstain} votes={abstain} color="bg-foam-500" />
      </div>

      <div className="p-4 rounded-xl border border-abyss-700 bg-abyss-850">
        {!isConnected ? (
          <button
            onClick={connectWallet}
            disabled={isConnecting}
            className="w-full py-3 rounded-xl font-semibold text-sm bg-tide-500 text-abyss-950 hover:bg-tide-400 disabled:opacity-60 transition-colors"
          >
            {isConnecting ? 'Connecting...' : 'Connect wallet to vote'}
          </button>
        ) : wrongNetwork ? (
          <button
            onClick={switchToTargetChain}
            className="w-full py-3 rounded-xl font-semibold text-sm bg-coral-500/15 text-coral-400 hover:bg-coral-500/25 transition-colors"
          >
            Switch to {TARGET_CHAIN_LABEL}
          </button>
        ) : stateNum === 1 ? (
          hasVoted ? (
            <div className="px-3 py-2 rounded-md bg-tide-400/10 text-tide-400 text-sm text-center">
              이미 투표했습니다.
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => castVote(1)}
                disabled={isBusy}
                className="py-2.5 rounded-lg text-sm font-semibold bg-tide-500/15 text-tide-400 hover:bg-tide-500/25 disabled:opacity-60 transition-colors"
              >
                For
              </button>
              <button
                onClick={() => castVote(0)}
                disabled={isBusy}
                className="py-2.5 rounded-lg text-sm font-semibold bg-coral-500/15 text-coral-400 hover:bg-coral-500/25 disabled:opacity-60 transition-colors"
              >
                Against
              </button>
              <button
                onClick={() => castVote(2)}
                disabled={isBusy}
                className="py-2.5 rounded-lg text-sm font-semibold bg-abyss-700 text-foam-300 hover:bg-abyss-600 disabled:opacity-60 transition-colors"
              >
                Abstain
              </button>
            </div>
          )
        ) : stateNum === 4 ? (
          <button
            onClick={handleQueue}
            disabled={isBusy}
            className="w-full py-3 rounded-xl font-semibold text-sm bg-tide-500 text-abyss-950 hover:bg-tide-400 disabled:opacity-60 transition-colors"
          >
            {isBusy ? 'Queuing...' : 'Queue in timelock'}
          </button>
        ) : stateNum === 5 ? (
          <button
            onClick={handleExecute}
            disabled={isBusy}
            className="w-full py-3 rounded-xl font-semibold text-sm bg-tide-500 text-abyss-950 hover:bg-tide-400 disabled:opacity-60 transition-colors"
          >
            {isBusy ? 'Executing...' : 'Execute (after timelock delay)'}
          </button>
        ) : (
          <div className="px-3 py-2 rounded-md bg-abyss-700 text-foam-500 text-sm text-center">
            {stateNum === 0 ? '투표 시작 대기 중입니다.' : `${STATE_LABEL[stateNum] ?? ''} 상태입니다.`}
          </div>
        )}
      </div>
    </div>
  );
}

function VoteBar({ label, pct, votes, color }: { label: string; pct: number; votes: bigint; color: string }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-foam-300 font-medium">{label}</span>
        <span className="text-foam-500 tabular-nums">
          {fmtVotes(votes)} ({pct.toFixed(1)}%)
        </span>
      </div>
      <div className="h-2 rounded-full bg-abyss-850 overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function CreateProposalForm({
  chainId,
  onCreated,
}: {
  chainId: number;
  onCreated: (proposal: ProposalLog) => void;
}) {
  const deployment = getDeployment(chainId);
  const [pool, setPool] = useState<MarketPair>(MARKET_PAIRS[0]);
  const [allocPoint, setAllocPoint] = useState('100');
  const [description, setDescription] = useState('');
  const [txError, setTxError] = useState<string | null>(null);

  const { writeContract, data: hash, isPending } = useWriteContract();
  const { data: receipt, isLoading: isConfirming, isSuccess: isConfirmed } = useWaitForTransactionReceipt({ hash });

  useEffect(() => {
    if (!isConfirmed || !receipt) return;
    for (const log of receipt.logs) {
      try {
        const decoded = decodeEventLog({ abi: GOVERNOR_ABI, data: log.data, topics: log.topics });
        if (decoded.eventName === 'ProposalCreated') {
          onCreated(decoded.args as unknown as ProposalLog);
          return;
        }
      } catch {
        // not the log we're looking for
      }
    }
  }, [isConfirmed]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = () => {
    if (!deployment?.governor || !deployment.farm) return;
    const pid = getFarmPid(pool.id, chainId);
    if (pid === undefined || !description.trim()) return;
    const points = BigInt(allocPoint || '0');

    const calldata = encodeFunctionData({
      abi: FARM_ABI,
      functionName: 'setAllocPoint',
      args: [BigInt(pid), points],
    });

    setTxError(null);
    writeContract(
      {
        address: deployment.governor,
        abi: GOVERNOR_ABI,
        functionName: 'propose',
        args: [[deployment.farm], [0n], [calldata], description.trim()],
      },
      { onError: (err) => setTxError(err.message) },
    );
  };

  return (
    <div className="p-4 border-b border-abyss-700 space-y-3">
      <div className="text-xs font-semibold text-foam-300">새 제안: 파밍 풀 보상 배율 변경</div>
      <select
        value={pool.id}
        onChange={(e) => setPool(MARKET_PAIRS.find((p) => p.id === e.target.value) ?? MARKET_PAIRS[0])}
        className="w-full px-3 py-2 rounded-md bg-abyss-850 border border-abyss-700 text-sm text-foam-100 outline-none focus:border-tide-500"
      >
        {MARKET_PAIRS.map((p) => (
          <option key={p.id} value={p.id}>
            {p.base}/{p.quote} LP
          </option>
        ))}
      </select>
      <input
        value={allocPoint}
        onChange={(e) => setAllocPoint(e.target.value.replace(/[^0-9]/g, ''))}
        placeholder="New allocPoint (e.g. 200)"
        className="w-full px-3 py-2 rounded-md bg-abyss-850 border border-abyss-700 text-sm text-foam-100 placeholder:text-foam-500 outline-none focus:border-tide-500"
      />
      <input
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="제안 설명"
        className="w-full px-3 py-2 rounded-md bg-abyss-850 border border-abyss-700 text-sm text-foam-100 placeholder:text-foam-500 outline-none focus:border-tide-500"
      />
      <button
        onClick={handleSubmit}
        disabled={isPending || isConfirming || !description.trim()}
        className="w-full py-2.5 rounded-lg text-sm font-semibold bg-tide-500 text-abyss-950 hover:bg-tide-400 disabled:opacity-60 transition-colors"
      >
        {isPending || isConfirming ? 'Submitting...' : 'Submit proposal on-chain'}
      </button>
      {txError && <div className="px-3 py-2 rounded-md bg-coral-500/15 text-coral-400 text-xs break-words">{txError}</div>}
    </div>
  );
}
